import { customizationParser, getCurrencyNumberOfDigits } from "./customization-helper";

// Helper function to truncate numbers to a specified number of decimals.
// Handles scientific notation (e.g. 5.115907697472721E-12 → 0, not 5.11).
export const truncateToDecimals = (num, decimals) => {
  const n = Number(num);
  if (typeof num !== "number" && typeof num !== "string") return 0;
  if (!Number.isFinite(n)) return 0;

  const decimalsNorm = Math.max(0, Math.floor(Number(decimals)) || 0);
  const factor = Math.pow(10, decimalsNorm);
  const threshold = 0.5 / factor;
  if (Math.abs(n) < threshold) return 0;

  return Math.floor(n * factor) / factor;
};

// Main currency formatter with truncation logic integrated
const customCurrencyFormatter = (amount, config = defaultConfig, currencyNumberOfDigits = null) => {

  if (currencyNumberOfDigits !== null && currencyNumberOfDigits !== undefined) {
    const truncatedAmount = truncateToDecimals(amount, currencyNumberOfDigits);
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: currencyNumberOfDigits,
      maximumFractionDigits: currencyNumberOfDigits,
    }).format(truncatedAmount);
  }

  const format = getFormatForAmount(amount, config);
  let truncatedAmount = amount;
  let formattedAmount = "";

  if (format === "##.##") {
    truncatedAmount = truncateToDecimals(amount, 2);
    formattedAmount = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(truncatedAmount);
  } else if (format === "###.##") {
    truncatedAmount = truncateToDecimals(amount, 2);
    formattedAmount = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(truncatedAmount);
  } else if (format === "###.#") {
    truncatedAmount = truncateToDecimals(amount, 1);
    formattedAmount = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(truncatedAmount);
  } else if (format === "####.#") {
    truncatedAmount = truncateToDecimals(amount, 1);
    formattedAmount = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(truncatedAmount);
  } else if (format === "#####") {
    truncatedAmount = truncateToDecimals(amount, 0);
    formattedAmount = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(truncatedAmount);
  } else if (format === "###K") {
    truncatedAmount = truncateToDecimals(amount / 1000, 0);
    formattedAmount = `${new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(truncatedAmount)}K`;
  } else if (format === "###M") {
    truncatedAmount = truncateToDecimals(amount / 1000000, 0);
    formattedAmount = `${new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(truncatedAmount)}M`;
  }

  return formattedAmount;
};

// Default configuration for formatting
const defaultConfig = {
  amountFormat: {
    0: "###.##", // Up to 99, format as 99.00
    1000: "###.##", // 100-999, format as 100.0 - 999.0
    10000: "###.##", // 1000-99999, format as 99999 (no decimal separator)
    100000: "###K", // 100000 - 999000, format as 100K
    1000000: "###M", // Over 1,000,000, format as 1M
  },
};

// Helper function to get the appropriate format for an amount
const getFormatForAmount = (amount, config) => {
  const formats = Object.keys(config.amountFormat)
    .map(Number)
    .sort((a, b) => b - a); // Sort in descending order

  for (let threshold of formats) {
    if (amount >= threshold) {
      return config.amountFormat[threshold];
    }
  }
  return config.amountFormat["0"]; // Default to the smallest format if no threshold is met
};

// Parses custom amount format configuration strings
const parseAmountFormatString = (formatStr) => {
  // Remove parentheses and split the string by '|'
  const rulesArray = formatStr.replace(/[()]/g, "").split("|");

  const customConfig = {
    amountFormat: {},
  };

  rulesArray.forEach((rule) => {
    const [range, format] = rule.split(",");
    const [min] = range.split("-").map(Number); // Get the minimum value of the range

    customConfig.amountFormat[min] = format; // Add the format with the min value as the key
  });

  return customConfig;
};

// Load custom configuration if available
let customConfig = customizationParser()?.amountFormat;

if (customConfig) {
  customConfig = parseAmountFormatString(customConfig);
}

// Exported currency formatter function
export const currencyFormatter = (number, format = null) => {
  const currencyNumberOfDigits = getCurrencyNumberOfDigits();
  if (format) {
    const truncatedNumber = truncateToDecimals(number, format);
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: format,
      maximumFractionDigits: format,
    }).format(truncatedNumber);
  } else {
    if (customConfig) {
      return customCurrencyFormatter(number, customConfig || defaultConfig);
    } else {
      return customCurrencyFormatter(number, defaultConfig, currencyNumberOfDigits);
    }
    
  }
};
    

// Helper: pretvaranje u minor units (centi) i nazad
const decimals = getCurrencyNumberOfDigits() ?? 2;
const scaler = 10 ** decimals;
export const toMinor = (amt) => Math.round(Number(amt) * scaler);
export const fromMinor = (minor) => minor / scaler;

export const percentOfMinor = (minor, pct) => {
  // "konzervativno" naniže:
  return Math.floor(minor * pct / 100);

  // ili standardno zaokruživanje:
  // return Math.round(minor * pct / 100);
};
